import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { BaseComponent } from '@shared/components/base.component';
import { NavigationEnd, ActivatedRoute } from '@angular/router';
import { TabLink } from '@shared/interfaces/menu/tab-link.interface';
import { CompanySettingsGuard } from '@core/guards/settings/company-settings.quard';
import { CompanyTeamGuard } from '@core/guards/settings/company-team.guard';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import {
  StickyFooterEvent,
  StickyFooterEventType,
  StickyFooterService
} from '@shared/services/sticky-footer.service';
import { SettingsService } from '@modules/settings/shared/services/settings.service';
import { BasePanelService } from '@shared/modules/base-panel/services/base-panel.service';
import { ConfirmationDiscardModalComponent } from '@shared/components/confirmation-discard-modal/confirmation-discard-modal.component';
import { Config } from '@shared/configs/config';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-settings-panel',
  templateUrl: './settings-panel.component.html',
  styleUrls: ['./settings-panel.component.scss']
})
export class SettingsPanelComponent extends BaseComponent implements OnInit, OnDestroy {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  bottomBarVisible: boolean = false;
  actionTitleVisible: boolean = false;
  formDirty = false;

  showProgressBar: boolean = true;
  tabLinks: TabLink[] = [];

  subSaving: Subscription;

  constructor(
    private active: ActivatedRoute,
    private service: SettingsService,
    public location: Location,
    private footerService: StickyFooterService,
    private basePanelService: BasePanelService,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.shouldShowProgressBar();
    this.initTabLinks();
    this.subscribeSaving();
    this.subscribeForFormDirty();
    this.basePanelService.blockScroll();
  }

  startSaving() {
    this.footerService.emitter.emit({ type: StickyFooterEventType.SUBMITTED });
  }

  subscribeSaving() {
    this.subSaving = this.footerService.emitter.subscribe((event: StickyFooterEvent) => {
      switch (event.type) {
        case StickyFooterEventType.START_SAVING:
          this.loading = true;
          break;
        case StickyFooterEventType.END_SAVING:
          this.service.changeFormDirty(false);
          this.loading = false;
          break;
        default:
          break;
      }
    });
  }

  subscribeForFormDirty() {
    this.service.isFormDirty$.subscribe((res) => {
      this.formDirty = res;
    });
  }

  shouldShowProgressBar() {
    this.checkRouteForProgressBar();
    this.setBottomBarVisibility();
    this.setActionTitleVisibility();
    if (event instanceof NavigationEnd) {
      this.checkRouteForProgressBar();
    }
    this.sub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setBottomBarVisibility();
        this.setActionTitleVisibility();
        this.service.changeFormDirty(false);
      }
    });
  }

  discardSaving() {
    let dialogRef = this.dialog.open(ConfirmationDiscardModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      switch (confirmed) {
        case true:
          this.footerService.emitter.emit({ type: StickyFooterEventType.DISCARD });
          break;
        default:
          break;
      }
    });
  }

  setBottomBarVisibility() {
    this.bottomBarVisible = !!this.active?.snapshot?.firstChild?.data?.bottomBar;
  }

  setActionTitleVisibility() {
    this.actionTitleVisible = !!this.active?.snapshot?.firstChild?.data?.actionTitle;
  }

  checkRouteForProgressBar() {
    this.active.firstChild.snapshot.data['hideProgressBar']
      ? (this.showProgressBar = false)
      : (this.showProgressBar = true);
  }

  initTabLinks() {
    const companySettingsGuard = new CompanySettingsGuard(this.store, this.n, this.s, this.t);
    const companyTeamGuard = new CompanyTeamGuard(this.store, this.n, this.s, this.t);

    this.tabLinks.push({
      name: this.t.instant('Settings.MenuV2.user'),
      link: this.n.getPath('user-settings')
    });

    this.tabLinks.push({
      name: this.t.instant('Settings.MenuV2.email'),
      link: this.n.getPath('email-settings')
    });

    if (companySettingsGuard.canActivate()) {
      this.tabLinks.push({
        name: this.t.instant('Settings.MenuV2.company'),
        link: this.n.getPath('company-settings')
      });
    }

    if (companyTeamGuard.canActivate()) {
      this.tabLinks.push({
        name: this.t.instant('Settings.MenuV2.team'),
        link: this.n.getPath('team-settings')
      });
    }

    this.tabLinks.push({
      name: this.t.instant('Settings.MenuV2.system'),
      link: this.n.getPath('system-settings')
    });
  }

  ngOnDestroy() {
    this.sub ? this.sub.unsubscribe() : null;
    this.subSaving ? this.subSaving.unsubscribe() : null;
    this.basePanelService.unBlockScroll();
  }
}
